/**
 * FuelInput Component
 * 
 * Specialized input component for fuel consumption with 2 decimal places
 * Handles input validation and formatting without crashes
 */

import React, { useState, useRef, useEffect } from "react";

interface FuelInputProps {
  label: string;
  value: number; // Value in L/lap
  onChange: (value: number) => void;
  placeholder?: string;
  disabled?: boolean;
  unit?: string;
  min?: number;
  max?: number;
}

export const FuelInput: React.FC<FuelInputProps> = ({
  label,
  value,
  onChange,
  placeholder = "3.20",
  disabled = false,
  unit = "L/lap",
  min = 0,
  max = 10
}) => {
  const [inputValue, setInputValue] = useState("");
  const lastValueRef = useRef(value);
  const isFocusedRef = useRef(false);

  // Format fuel consumption to 2 decimal places
  const formatFuel = (fuelValue: number): string => {
    if (!fuelValue || fuelValue <= 0) return "";
    return fuelValue.toFixed(2);
  };

  // Parse fuel consumption from string
  const parseFuel = (fuelString: string): number => {
    if (!fuelString || fuelString.trim() === '') {
      return 0;
    }

    // Clean input - only allow numbers and decimal point
    const cleanInput = fuelString.replace(/[^0-9.]/g, '');
    
    // Handle multiple decimal points
    const parts = cleanInput.split('.');
    if (parts.length > 2) {
      return 0; // Invalid format
    }

    const numericValue = parseFloat(cleanInput);
    if (isNaN(numericValue)) {
      return 0;
    }

    // Apply min/max constraints
    if (min !== undefined && numericValue < min) return min;
    if (max !== undefined && numericValue > max) return max;

    return numericValue;
  };

  // Initialize input value when component mounts or value changes externally
  useEffect(() => {
    if (!isFocusedRef.current && lastValueRef.current !== value) {
      setInputValue(formatFuel(value));
      lastValueRef.current = value;
    }
  }, [value]);

  // Initialize on mount
  useEffect(() => {
    if (value > 0) {
      setInputValue(formatFuel(value));
      lastValueRef.current = value;
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputValue = e.target.value;
    setInputValue(newInputValue);

    if (newInputValue.trim() === '') {
      onChange(0);
      lastValueRef.current = 0;
      return;
    }

    const parsedValue = parseFuel(newInputValue);
    if (parsedValue > 0) {
      onChange(parsedValue);
      lastValueRef.current = parsedValue;
    }
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    const parsedValue = parseFuel(inputValue);
    if (parsedValue > 0) {
      const formattedValue = formatFuel(parsedValue);
      setInputValue(formattedValue);
      onChange(parsedValue);
      lastValueRef.current = parsedValue;
    } else if (inputValue.trim() !== '') {
      // If user typed something but it's invalid, keep the input as-is
      // Don't clear it, just don't update the parent
      setInputValue(inputValue);
    } else {
      // Only clear if the input is completely empty
      setInputValue("");
      onChange(0);
      lastValueRef.current = 0;
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-slate-300">
        {label}
        {unit && <span className="text-slate-400 ml-1">({unit})</span>}
      </label>
      
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 bg-slate-800 rounded-xl border border-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
    </div>
  );
};
